import { z } from 'zod';

import { ZodCurrency } from '@Common/types/Amount';
import { ZodBackofficeGroupsT } from '@Common/types/bo/authorization';
import { ZodB2bCorrectionType } from '@Common/types/bo/b2b/ops_actions/corrections';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { DeltaModelName } from '@Common/types/bo/delta_alerts';
import { TaskActionIdentityReason } from '@Common/types/bo/tasks/proof_identity';
import { ZodUserAccountTypeBo } from '@Common/types/bo/users/users';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import {
  ZodBoTaskSnoozeUntil,
  ZodBoTaskSnoozeUntilInput,
} from '@Common/types/bo/utils/zod_bo_task_snooze_until';
import { ZodCampaignType } from '@Common/types/explore/employee_savings_campaign';
import { ZodOnboardingPath } from '@Common/types/flows/onboarding';
import { IdPathParam, ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import {
  // @ts-expect-error used once compiled by TSC, which significantly improves performance
  type EndpointConfig,
  endpoint,
  GetEndpointType,
} from '@Common/types/zod/zod-endpoint';
import { ZodJSONObject } from '@Common/types/zod/zod-json';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export type TasksTypes = (typeof taskTypes)[number];

export type GenericTasksTypes = (typeof genericTasksTypes)[number];
export type AmlTasksTypes = (typeof amlTasksTypes)[number];
export type ModerationTaskTypes = (typeof moderationTasksTypes)[number];
export type MlTaskTypes = (typeof mlTasksTypes)[number];
export type CorporateActionTasksTypes = (typeof corporateActionsTasksType)[number];
export type B2bTaskTypes = (typeof b2bTasksTypes)[number];
export type HiddenTaskTypes = (typeof hiddenTasksTypes)[number];

export const moderationTasksTypes = ['flagged_activity', 'flagged_user'] as const;

export const amlTasksTypes = [
  'aml_count_big_fundings_last_month',
  'aml_sum_transfers_last_month',
  'aml_count_transfers_last_month',
  'aml_count_transfers_last_day',
  'aml_sum_funds_move_last_month',
] as const;

export const mlTasksTypes = ['ml_alert'] as const;

export const corporateActionsTasksType = ['corporate_action'] as const;

export const genericTasksTypes = [
  'proof_address',
  'kyc_ca_failed',
  'kyc_uniqueness_failed',
  'kyc_identity_failed',
  'kyc_transaction_reporting_id_duplicate',
  'screening_monitoring',
  'screening_monitoring_v2',
  'mdlr_bank_name_failed',
  'manual_proof_identity',
  'source_of_funds',
  'risk_score_updated',
  'manual_proof_address',
  'periodic_review',
  'business_proof_identity',
  'business_screening',
  'proof_identity',
  'manual_source_of_funds',
  'manual_proof_identity_v2',
  'unmatched_payment',
  'wm_proof_identity',
  'wm_business_proof_identity',
  'pea_transfer',
] as const;

export const b2bTasksTypes = [
  'address_change_review',
  'b2b_source_of_funds',
  'b2b_procedure',
  'b2b_eckert',
  'b2b_screening_monitoring',
  'b2b_proof_identity',
  'b2b_payment_compliance_review',
] as const;

// Tasks that can't be listed, only accessed by ID
export const hiddenTasksTypes = [
  'b2b_withdrawal',
  'b2b_transfer_request',
  'b2c_transfer_request',
  'b2b_correction',
] as const;

export const taskTypes = [
  ...genericTasksTypes,
  ...amlTasksTypes,
  ...moderationTasksTypes,
  ...mlTasksTypes,
  ...corporateActionsTasksType,
  ...b2bTasksTypes,
  ...hiddenTasksTypes,
] as const;

export const ZodTaskTypes = z.enum(taskTypes);

export const taskTypesDeprecated = [
  'flagged_community_comment',
  'flagged_community_post',
  'flagged_community_member',
  'high_reach_content',
  'unusual_activity_report',
] as const;
export type TaskTypesDeprecated = (typeof taskTypesDeprecated)[number];

export const genericTasksTypesAlsoB2b = [
  'mdlr_bank_name_failed',
  'unmatched_payment',
] satisfies GenericTasksTypes[];

export type AmlThresholdLabels =
  | 'count_big_fundings_last_month'
  | 'sum_transfers_last_month'
  | 'count_transfers_last_month'
  | 'count_transfers_last_day'
  | 'sum_funds_move_last_month';

export const ZodTaskActionType = z.enum([
  'RESOLVED',
  'REJECTED',
  'TO_CUSTOMER',
  'PENDING',
  'REOPEN',
]);
export type TaskActionType = z.infer<typeof ZodTaskActionType>;

export const TaskStateValues = ['PENDING', 'AWAITING_CUSTOMER', 'RESOLVED', 'REJECTED'] as const;
export type TaskState = (typeof TaskStateValues)[number];
export const ZodTaskStates = z.enum(TaskStateValues);

export const taskActionPoaApproveReason = ['APPROVE_VALID_DOCUMENT'] as const;
export const ZodTaskActionPoaApproveReason = z.enum(taskActionPoaApproveReason);
export type TaskActionPoaApproveReason = z.infer<typeof ZodTaskActionPoaApproveReason>;

export const taskActionPoaRetryReason = [
  'RETRY_ADDRESS_MISMATCH',
  'RETRY_NAME_MISMATCH',
  'RETRY_DOCUMENT_NOT_SUPPORTED',
  'RETRY_RANDOM_UPLOAD',
  'RETRY_OLD_DOCUMENT',
  'RETRY_INCOMPLETE_DOCUMENT',
  'RETRY_POOR_IMAGE_QUALITY',
  'RETRY_OTHER',
] as const;
export const ZodTaskActionPoaRetryReason = z.enum(taskActionPoaRetryReason);
export type TaskActionPoaRetryReason = z.infer<typeof ZodTaskActionPoaRetryReason>;

export const taskActionPoaRejectReason = ['REJECTION_FRAUD', 'REJECTION_OTHER'] as const;
export const ZodTaskActionPoaRejectReason = z.enum(taskActionPoaRejectReason);
export type TaskActionPoaRejectReason = z.infer<typeof ZodTaskActionPoaRejectReason>;

export const ZodTaskActionPoaReason = z.enum([
  ...taskActionPoaApproveReason,
  ...taskActionPoaRetryReason,
  ...taskActionPoaRejectReason,
]);
export type TaskActionPoaReason = z.infer<typeof ZodTaskActionPoaReason>;

export const boTaskUserRiskValues = [
  '1_low',
  '2_medium',
  '3_high',
  '4_suggested_rejection',
] as const;
export type BOTaskUserRiskValues = (typeof boTaskUserRiskValues)[number];

export const ZodBOTaskBasicFields = z.object({
  id: ZodId,
  user_id: ZodId,
  post_id: ZodId.nullable(),
  state: ZodTaskStates,
  sub_state: ZodTrimmedString.nullable(),
  created_at: ZodDateTimeString,
  updated_at: ZodDateTimeString,
  resolver_id: ZodId.nullable(),
  resolver_email: ZodTrimmedString.nullable(),
  resolved_at: ZodDateTimeString.nullable(),
  archived: z.boolean(),
  assignee_group_id: ZodId,
  assignee_group_email: ZodBackofficeGroupsT.nullable(),
  assignee_id: ZodId.nullable(),
  assignee_email: ZodTrimmedString.nullable(),
  ca_ref: ZodTrimmedString.nullable(),
  additional_info: ZodTrimmedString.nullable(),
  // user fields
  username: ZodTrimmedString.nullable(),
  display_name: ZodTrimmedString.nullable(),
  email: ZodTrimmedString.nullable(),
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
  legalname: ZodTrimmedString.nullable(),
  date_of_birth: ZodDateTimeString.nullable(),
  risk_score: z.number().nullable(),
  risk: z.enum(boTaskUserRiskValues).nullable(), // Cannot import from Users, so recreating
  onboarding_risk_score: z.number().nullable(),
  onboarding_risk: z.enum(boTaskUserRiskValues).nullable(), // Cannot import from Users, so recreating
  onboarding_path: ZodOnboardingPath.nullable(),
  currency: ZodCurrency.nullable(),
  mdlr_beneficiaries_id: ZodId.nullable(),
  mdlr_payment_id: ZodId.nullable(),
  user_report_id: ZodId.nullable(),
  reporter_user_id: ZodId.nullable(),
  reason: ZodTrimmedString.nullable(),
  report_reason: ZodTrimmedString.nullable(),
  delta_alert_id: ZodTrimmedString.nullable(),
  delta_model_name: ZodTrimmedString.nullable(),
  delta_alert_probability: z.number().nullable(),
  snooze_until: ZodBoTaskSnoozeUntil.nullable(),
  is_user_active: z.boolean(),
  account_type: ZodUserAccountTypeBo.nullable(),
  investment_account_id: ZodId.nullable(),
  destination_account_warning: z
    .enum(['high_risk_country', 'country_under_surveillance'])
    .nullable(),
  metadata: ZodJSONObject.nullable(),
  b2b_employee_id: ZodId.nullable(),
  od_order_correlation_id: ZodTrimmedString.nullable(),
  is_retried: z.boolean(),
});

const ZodSharedIbanHolder = z.object({
  holder_type: z.enum(['user', 'company']),
  user_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
  bnmf_task_id: ZodId.nullable(),
});
export type SharedIbanHolder = z.infer<typeof ZodSharedIbanHolder>;

const ZodSepaApprovalDebit = z.discriminatedUnion('resource', [
  z.object({
    resource: z.literal('savings_funds_request'),
    id: ZodId,
    type: z.literal('matching_contribution'),
    total_net_amount: z.number().int(),
  }),
  z.object({
    resource: z.literal('b2b_campaign'),
    id: ZodId,
    type: ZodCampaignType,
    total_net_amount: z.number().int().nullable(),
  }),
]);
export type SepaApprovalDebit = z.infer<typeof ZodSepaApprovalDebit>;

const ZodBoBNFMTask = z.object({
  ...ZodBOTaskBasicFields.shape,
  type: z.literal('mdlr_bank_name_failed'),
  user_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
  mdlr_beneficiaries_id: ZodId.nullable(),
  co_sepa_mandate_id: ZodId.nullable(),
  shared_iban_holders: z.array(ZodSharedIbanHolder),
  // null when the task is not a pending company SEPA mandate task
  sepa_approval_debits: z.array(ZodSepaApprovalDebit).nullable(),
});

const ZodBoUnmatchedPaymentTask = z.object({
  ...ZodBOTaskBasicFields.shape,
  type: z.literal('unmatched_payment'),
  amount: z.number().int(),
  currency: ZodCurrency,
  reference: ZodTrimmedString.nullable(),
  remittance_info: ZodTrimmedString.nullable(),
  direction: z.enum(['in', 'out']),
  executed_at: ZodDateTimeString.nullable(),
  account_type: z.enum(['COI', 'TEC']),
  // For IN payments (deposits)
  payer_details: z
    .object({
      name: ZodTrimmedString.nullable(),
      iban: ZodTrimmedString.nullable(),
      bic: ZodTrimmedString.nullable(),
    })
    .nullable(),
  // For OUT payments (payouts)
  payee_details: z
    .object({
      name: ZodTrimmedString.nullable(),
      iban: ZodTrimmedString.nullable(),
      bic: ZodTrimmedString.nullable(),
    })
    .nullable(),
  mdlr_payment_id: ZodId,
  user_id: z.null(),
});
export type BoUnmatchedPaymentTask = z.infer<typeof ZodBoUnmatchedPaymentTask>;

export const ZodBoB2bTransferRequestTask = z.object({
  ...ZodBOTaskBasicFields.shape,
  type: z.literal('b2b_transfer_request'),
  user_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
});

export const ZodBoB2cTransferRequestTask = z.object({
  ...ZodBOTaskBasicFields.shape,
  type: z.literal('b2c_transfer_request'),
  user_id: ZodId.nullable(),
});

export const ZodBoB2bCorrectionTask = z.object({
  ...ZodBOTaskBasicFields.shape,
  type: z.literal('b2b_correction'),
  user_id: ZodId.nullable(),
  b2b_company_id: ZodId.nullable(),
  correction_type: ZodB2bCorrectionType,
});

export const ZodBoMlAlertTask = z.object({
  ...ZodBOTaskBasicFields.shape,
  type: z.literal('ml_alert'),
  user_id: ZodId.nullable(),
});

const nonBoUserTaskTypes = [
  'mdlr_bank_name_failed',
  'b2b_transfer_request',
  'b2b_correction',
  'b2c_transfer_request',
  'ml_alert',
] as const;

export const ZodBoUserTask = z.object({
  ...ZodBOTaskBasicFields.shape,
  type: ZodTaskTypes.exclude([...nonBoUserTaskTypes, 'unmatched_payment']),
  screening_risk_score: z.number().nullable(),
  user_archived: z.boolean(),
});

export const ZodBOTask = z.discriminatedUnion('type', [
  ZodBoBNFMTask,
  ZodBoUnmatchedPaymentTask,
  ZodBoB2bTransferRequestTask,
  ZodBoB2cTransferRequestTask,
  ZodBoB2bCorrectionTask,
  ZodBoUserTask,
  ZodBoMlAlertTask,
]);

export type BoUserTask = z.infer<typeof ZodBoUserTask>;
export type BoBNMFTask = z.infer<typeof ZodBoBNFMTask>;
export type BoB2bTransferRequestTask = z.infer<typeof ZodBoB2bTransferRequestTask>;
export type BOTask = z.infer<typeof ZodBOTask>;

export function isBNMFBOTask(task: BOTask): task is BoBNMFTask {
  return task.type === 'mdlr_bank_name_failed';
}

export function isUserBoTask(task: BOTask): task is BoUserTask {
  return !(nonBoUserTaskTypes as readonly string[]).includes(task.type);
}

export const ZodBOTasks = z.array(
  z.object({
    ...ZodBoUserTask.merge(ZodBoBNFMTask).omit({
      ca_ref: true,
      screening_risk_score: true,
      metadata: true,
      destination_account_warning: true,
      shared_iban_holders: true,
      sepa_approval_debits: true,
    }).shape,
    type: ZodTaskTypes,
    is_iban_shared: z.boolean(),
  }),
);
export type BoTasks = z.infer<typeof ZodBOTasks>;

export type KeysOfUnion<T> = T extends T ? keyof T : never;

/**
 * Field names found on a `metadata` payload or synthesized for a specific task type's UI
 * (e.g. re-aliased from `metadata`), rather than declared directly on `BOTask`.
 */
export type BOTaskExtraFieldName =
  | 'is_meeting_criteria'
  | 'b2b_procedure_type'
  | 'creator_email'
  | 'creator_type';

export type BOTaskFieldName = KeysOfUnion<BOTask> | keyof BoTasks[number] | BOTaskExtraFieldName;

export const getBoTasksQuerySortColumns: KeysOfUnion<BOTask>[] = [
  'id',
  'user_id',
  'b2b_company_id',
  'b2b_employee_id',
  'post_id',
  'type',
  'state',
  'created_at',
  'updated_at',
  'resolver_id',
  'resolved_at',
  'archived',
  'assignee_group_id',
  'assignee_group_email',
  'assignee_id',
  'assignee_email',
  'additional_info',
  // user fields
  'username',
  'display_name',
  'firstname',
  'lastname',
  'account_type',
  'date_of_birth',
  'risk_score',
  'onboarding_path',
  'mdlr_beneficiaries_id',
  'co_sepa_mandate_id',
  'user_report_id',
  'reporter_user_id',
  'reason',
  'report_reason',
  'delta_alert_id',
  'delta_model_name',
  'delta_alert_probability',
];

export const ZodGetBOTasksQueryFilter = z
  .object({
    id: createZodArrayable(ZodId),
    user_id: createZodArrayable(ZodId),
    b2b_company_id: createZodArrayable(ZodId),
    post_id: createZodArrayable(ZodId),
    type: ZodTaskTypes.or(z.array(ZodTaskTypes)),
    not_type: ZodTaskTypes.or(z.array(ZodTaskTypes)).optional(),
    state: ZodTaskStates.or(z.array(ZodTaskStates)),
    created_at: ZodTrimmedString,
    resolver_id: createZodArrayable(ZodId),
    resolver_email: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    resolved_at: ZodTrimmedString.nullable(),
    archived: z.boolean(),
    assignee_group_id: createZodArrayable(ZodId),
    assignee_group_email: ZodBackofficeGroupsT.or(z.array(ZodBackofficeGroupsT)),
    assignee_id: createZodArrayable(ZodId),
    assignee_email: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    username: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    display_name: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    firstname: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    lastname: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    legalname: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    date_of_birth: ZodTrimmedString,
    risk_score: z.number().or(z.array(z.number())),
    risk: z.enum(boTaskUserRiskValues).or(z.array(z.enum(boTaskUserRiskValues))),
    onboarding_path: ZodOnboardingPath.or(z.array(ZodOnboardingPath)),
    mdlr_beneficiaries_id: createZodArrayable(ZodId),
    mdlr_payment_id: createZodArrayable(ZodId),
    co_sepa_mandate_id: createZodArrayable(ZodId),
    user_report_id: createZodArrayable(ZodId),
    reporter_user_id: createZodArrayable(ZodId),
    reason: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    report_reason: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    delta_alert_id: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    delta_model_name: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    delta_alert_probability: z.number().or(z.array(z.number())),
    user_archived: z.boolean(),
    is_user_active: z.boolean(),
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
    account_type: ZodUserAccountTypeBo,
    od_order_correlation_id: ZodTrimmedString.or(z.array(ZodTrimmedString)),
    is_retried: z.boolean(),
    is_iban_shared: z.boolean(),
  })
  .partial();

export const ZodGetTasksQuery = buildZodBoListQueryParsed(
  ZodGetBOTasksQueryFilter,
  getBoTasksQuerySortColumns,
  { range: [0, 5] },
);
export const ZodGetTasksResponse = ZodBOTasks;

export type GetBOTasksQueryFilter = z.infer<typeof ZodGetBOTasksQueryFilter>;

export type GetBOTasksQuery = BoListQueryBase<
  GetBOTasksQueryFilter,
  (typeof getBoTasksQuerySortColumns)[number]
>;

export type CipKycApprovedReason =
  | 'CA_EXPERT'
  | 'CA_PEP'
  | 'CA_FIRST_LEVEL'
  | TaskActionIdentityReason;

/**
 * GET /apibo/tasks/:id
 */

export const ZodGetTasksOneResponse = ZodBOTask;
export type GetTasksOneResponse = z.infer<typeof ZodGetTasksOneResponse>;

export const getTasksIdEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodGetTasksOneResponse,
  pathParams: ZodIDPathParam,
});

export type GetTasksIdEndpoint = GetEndpointType<typeof getTasksIdEndpoint>;

export const getTasksEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetTasksQuery,
  responseBody: ZodGetTasksResponse,
});

export type GetTasksEndpoint = GetEndpointType<typeof getTasksEndpoint>;
/**
 * GET /apibo/tasks
 */

export type GetTasksResponse = BoTasks;
export type CreateTasksRoutesParams = {
  includeTypes: TasksTypes[] | readonly TasksTypes[];
  includeDeltaModelNames?: DeltaModelName[] | readonly DeltaModelName[];
};

/**
 * PUT /apibo/tasks/archive_task/:id
 */

const ZodPutArchiveTaskTypes = z.enum([
  'proof_address',
  'kyc_ca_failed',
  'kyc_uniqueness_failed',
  'flagged_activity',
  ...amlTasksTypes,
]);

export type PutArchiveTaskTypes = z.infer<typeof ZodPutArchiveTaskTypes>;

// ensure that ZodPutArchiveTaskTypes values are assignable to TasksTypes
export const putArchiveTaskTypes: TasksTypes[] = ZodPutArchiveTaskTypes.options;

const ZodPutArchiveTaskRequestBody = z.object({
  type: ZodPutArchiveTaskTypes,
});

export type PutArchiveTaskRequestBody = z.infer<typeof ZodPutArchiveTaskRequestBody>;

const ZodPutArchiveTaskResponse = z.object({
  id: ZodId,
});

export type PutArchiveTaskResponse = z.infer<typeof ZodPutArchiveTaskResponse>;

export const putTasksArchiveTaskEndpoint = endpoint({
  method: 'PUT',
  responseBody: ZodPutArchiveTaskResponse,
  body: ZodPutArchiveTaskRequestBody,
  pathParams: ZodIDPathParam,
});

export type PutTasksArchiveTaskEndpoint = GetEndpointType<typeof putTasksArchiveTaskEndpoint>;

/**
 * PUT /apibo/tasks/assign/:id
 */
const ZodPutAssignTaskRequest = z.object({
  assignee_group_email: ZodBackofficeGroupsT,
});
export type PutAssignTaskRequest = z.infer<typeof ZodPutAssignTaskRequest>;

const ZodPutAssignTaskResponse = z.object({
  id: ZodId,
  assignee_group_email: ZodBackofficeGroupsT,
});
export type PutAssignTaskResponse = z.infer<typeof ZodPutAssignTaskResponse>;

export type UsersDocumentsSubtype = (typeof UsersDocumentsSubtypeValues)[number];

export const UsersDocumentsSubtypeValues = [
  'utility_bill',
  'bank_account_statement',
  'mortgage_statement',
  'internet_landline_bill',
  'council_tax_bill',
  'council_tax_reminder',
  'driving_license',
  'contract_holder_certificate',
  'subscription_proof',
  'rent_receipt',
  'housing_tax',
  'property_tax',
  'income_tax_return_tax_notice',
  'national_identity_card',
  'internet_tv_bill',
  'other',
  'company_tax_statement',
] as const;

/**
 * PUT /apibo/tasks/poa/reject/:id
 */
export const ZodPutRejectPoaTaskRequest = z.object({
  reason: ZodTaskActionPoaRejectReason,
});

export type PutRejectPoaTaskRequest = z.infer<typeof ZodPutRejectPoaTaskRequest>;

export const ZodPutRejectPoaTaskResponse = z.object({
  id: ZodId,
  user_id: ZodId,
  post_id: ZodId.nullable(),
  type: z.literal('proof_address'),
  state: z.literal('REJECTED'),
});

export type PutRejectPoaTaskResponse = z.infer<typeof ZodPutRejectPoaTaskResponse>;
/**
 * PUT /apibo/tasks/poa/approve/:id
 */

export const ZodPutApprovePoaTaskRequest = z.object({
  reason: ZodTaskActionPoaApproveReason,
});
export type PutApprovePoaTaskRequest = z.infer<typeof ZodPutApprovePoaTaskRequest>;

export const ZodPutApprovePoaTaskResponse = z.object({
  id: ZodId,
});
export type PutApprovePoaTaskResponse = z.infer<typeof ZodPutApprovePoaTaskResponse>;

/**
 * PUT /apibo/tasks/poa/retry/:id
 */

export const ZodPutRetryPoaTaskRequest = z.object({
  reason: ZodTaskActionPoaRetryReason,
  snooze_until: ZodBoTaskSnoozeUntilInput,
});

export type PutRetryPoaTaskRequest = z.infer<typeof ZodPutRetryPoaTaskRequest>;

export const ZodPutRetryPoaTaskResponse = z.object({
  id: ZodId,
  user_id: ZodId,
  post_id: ZodId.nullable(),
  type: z.literal('proof_address'),
  state: z.literal('RESOLVED'),
});

export type PutRetryPoaTaskResponse = z.infer<typeof ZodPutRetryPoaTaskResponse>;

/**
 * PUT /apibo/tasks/self_assign/:id
 */

export type PutSelfAssignTaskParams = IdPathParam;

const ZodPutSelfAssignTaskResponse = z.object({
  id: ZodId,
  assignee_id: ZodId,
  assignee_email: ZodTrimmedString,
});

export type PutSelfAssignTaskResponse = z.infer<typeof ZodPutSelfAssignTaskResponse>;

/**
 * PUT /apibo/tasks/await_customer/:id
 */

const ZodPutAwaitCustomerTaskRequest = z.object({
  type: ZodTaskTypes,
  snooze_until: ZodBoTaskSnoozeUntilInput,
});

export type PutAwaitCustomerTaskRequest = z.infer<typeof ZodPutAwaitCustomerTaskRequest>;

const ZodPutAwaitCustomerTaskResponse = z.object({
  id: ZodId,
});

export type PutAwaitCustomerTaskResponse = z.infer<typeof ZodPutAwaitCustomerTaskResponse>;

export type PutAwaitCustomerTaskParams = IdPathParam;

export const putAwaitCustomerTaskEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutAwaitCustomerTaskRequest,
  responseBody: ZodPutAwaitCustomerTaskResponse,
});

export type PutAwaitCustomerTaskEndpoint = GetEndpointType<typeof putAwaitCustomerTaskEndpoint>;

/**
 * PUT /apibo/tasks/assign_bo_user/:id
 */
export const ZodPutAssignTaskBoUserRequest = z.object({
  assignee_id: ZodId,
});

export const ZodPutAssignTaskBoUserResponse = z.object({
  id: ZodId,
  assignee_id: ZodId,
  assignee_email: ZodTrimmedString,
});

export const putAssignTaskBoUserEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutAssignTaskBoUserRequest,
  responseBody: ZodPutAssignTaskBoUserResponse,
});

export type PutAssignTaskBoUserEndpoint = GetEndpointType<typeof putAssignTaskBoUserEndpoint>;

/**
 * PUT /apibo/tasks/pending/:id
 */

const ZodPutPendingTaskRequest = z.object({
  type: ZodTaskTypes,
});

export type PutPendingTaskRequest = z.infer<typeof ZodPutPendingTaskRequest>;

const ZodPutPendingTaskResponse = z.object({
  id: ZodId,
});
export type PutPendingTaskResponse = z.infer<typeof ZodPutPendingTaskResponse>;

export type PutPendingTaskParams = IdPathParam;

/**
 * PUT /apibo/tasks/uar
 */

export const submitUarTaskReason = ['submit'] as const;
export const discardUarTaskReason = ['no_suspicious_activity', 'tc_breach'] as const;

export const ZodSubmitUarTaskReason = z.enum(submitUarTaskReason);
export type SubmitUarTaskReason = z.infer<typeof ZodSubmitUarTaskReason>;

export const ZodDiscardUarTaskReason = z.enum(discardUarTaskReason);
export type DiscardUarTaskReason = z.infer<typeof ZodDiscardUarTaskReason>;

const ZodPutActionUarTaskResponse = z.object({
  id: ZodId,
});

export type PutActionUarTaskResponse = z.infer<typeof ZodPutActionUarTaskResponse>;

const ZodPutSubmitUarTaskRequestBody = z.object({
  reason: ZodSubmitUarTaskReason,
});

export type PutSubmitUarTaskRequestBody = z.infer<typeof ZodPutSubmitUarTaskRequestBody>;

const ZodPutDiscardUarTaskRequestBody = z.object({
  reason: ZodDiscardUarTaskReason,
});

export type PutDiscardUarTaskRequestBody = z.infer<typeof ZodPutDiscardUarTaskRequestBody>;

export const taskActionSofApproveReason = ['APPROVE_VALID_DOCUMENT'] as const;

export const ZodTaskActionSofApproveReason = z.enum(taskActionSofApproveReason);

export type TaskActionSofApproveReason = z.TypeOf<typeof ZodTaskActionSofApproveReason>;

export const taskActionSofRejectReason = ['REJECTION_FRAUD', 'REJECTION_OTHER'] as const;

export const ZodTaskActionSofRejectReason = z.enum(taskActionSofRejectReason);

export type TaskActionSofRejectReason = z.TypeOf<typeof ZodTaskActionSofRejectReason>;

export const taskActionSofRetryReason = [
  'RETRY_ADDRESS_MISMATCH',
  'RETRY_NAME_MISMATCH',
  'RETRY_DOCUMENT_NOT_SUPPORTED',
  'RETRY_RANDOM_UPLOAD',
  'RETRY_OLD_DOCUMENT',
  'RETRY_INCOMPLETE_DOCUMENT',
  'RETRY_POOR_IMAGE_QUALITY',
  'RETRY_OTHER',
] as const;

export const ZodTaskActionSofRetryReason = z.enum(taskActionSofRetryReason);

export type TaskActionSofRetryReason = z.TypeOf<typeof ZodTaskActionSofRetryReason>;

export const taskActionSofReason = [
  ...taskActionSofApproveReason,
  ...taskActionSofRejectReason,
  ...taskActionSofRetryReason,
] as const;

export const ZodTaskActionSofReason = z.enum(taskActionSofReason);

export type TaskActionSofReason = z.TypeOf<typeof ZodTaskActionSofReason>;

export const sofReasonMap: Record<TaskActionSofReason, string> = {
  APPROVE_VALID_DOCUMENT: 'Approve - Valid document',
  RETRY_ADDRESS_MISMATCH: 'Retry - Address mismatch',
  RETRY_NAME_MISMATCH: 'Retry - Name mismatch',
  RETRY_DOCUMENT_NOT_SUPPORTED: 'Retry - Not supported document',
  RETRY_RANDOM_UPLOAD: 'Retry - Random upload',
  RETRY_OLD_DOCUMENT: 'Retry - Old document',
  RETRY_INCOMPLETE_DOCUMENT: 'Retry - Incomplete document',
  RETRY_POOR_IMAGE_QUALITY: 'Retry - Poor image quality',
  RETRY_OTHER: 'Retry - Other',
  REJECTION_FRAUD: 'Deny User - Fraud',
  REJECTION_OTHER: 'Deny User - Other',
};

export const ZodTaskSofLimit = z.number().int().min(100);

/**
 * PUT /apibo/tasks/sof/approve/:id
 */

const ZodPutApproveSofTaskRequest = z.object({
  reason: ZodTaskActionSofReason,
  sof_yearly_income_limit: ZodTaskSofLimit,
  sof_savings_limit: ZodTaskSofLimit,
});

export type PutApproveSofTaskRequest = z.TypeOf<typeof ZodPutApproveSofTaskRequest>;

const ZodPutApproveSofTaskResponse = z.object({
  id: ZodId,
});

export type PutApproveSofTaskResponse = z.TypeOf<typeof ZodPutApproveSofTaskResponse>;

/**
 * PUT /apibo/tasks/sof/reject/:id
 */

const ZodPutRejectSofTaskRequest = z.object({
  reason: ZodTaskActionSofReason,
  sof_yearly_income_limit: ZodTaskSofLimit,
  sof_savings_limit: ZodTaskSofLimit,
});

export type PutRejectSofTaskRequest = z.TypeOf<typeof ZodPutRejectSofTaskRequest>;

const ZodPutRejectSofTaskResponse = z.object({
  id: ZodId,
  user_id: ZodId,
  post_id: ZodId.nullable(),
  type: ZodTaskTypes,
  state: ZodTaskStates,
});

export type PutRejectSofTaskResponse = z.TypeOf<typeof ZodPutRejectSofTaskResponse>;

/**
 * PUT /apibo/tasks/sof/retry/:id
 */

const ZodRetrySofTaskRequest = z.object({
  reason: ZodTaskActionSofReason,
});

export type PutRetrySofTaskRequest = z.TypeOf<typeof ZodRetrySofTaskRequest>;

const ZodPutRetrySofTaskResponse = z.object({
  id: ZodId,
  user_id: ZodId,
  post_id: ZodId.nullable(),
  type: ZodTaskTypes,
  state: ZodTaskStates,
});

export type PutRetrySofTaskResponse = z.TypeOf<typeof ZodPutRetrySofTaskResponse>;

export const putTasksSelfAssignIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  responseBody: ZodPutSelfAssignTaskResponse,
});

export type PutTasksSelfAssignIdEndpoint = GetEndpointType<typeof putTasksSelfAssignIdEndpoint>;

export const putTasksSofRetryIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodRetrySofTaskRequest,
  responseBody: ZodPutRetrySofTaskResponse,
});

export type PutTasksSofRetryIdEndpoint = GetEndpointType<typeof putTasksSofRetryIdEndpoint>;

export const putTasksSofApproveIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutApproveSofTaskRequest,
  responseBody: ZodPutApproveSofTaskResponse,
});

export type PutTasksSofApproveIdEndpoint = GetEndpointType<typeof putTasksSofApproveIdEndpoint>;

export const putTasksSofRejectIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutRejectSofTaskRequest,
  responseBody: ZodPutRejectSofTaskResponse,
});

export type PutTasksSofRejectIdEndpoint = GetEndpointType<typeof putTasksSofRejectIdEndpoint>;

export const putTasksPendingIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutPendingTaskRequest,
  responseBody: ZodPutPendingTaskResponse,
});

export type PutTasksPendingIdEndpoint = GetEndpointType<typeof putTasksPendingIdEndpoint>;

export const putTasksPoaRetryIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutRetryPoaTaskRequest,
  responseBody: ZodPutRetryPoaTaskResponse,
});

export type PutTasksPoaRetryIdEndpoint = GetEndpointType<typeof putTasksPoaRetryIdEndpoint>;

export const putTasksPoaApproveIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutApprovePoaTaskRequest,
  responseBody: ZodPutApprovePoaTaskResponse,
});

export type PutTasksPoaApproveIdEndpoint = GetEndpointType<typeof putTasksPoaApproveIdEndpoint>;

export const putTasksPoaRejectIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutRejectPoaTaskRequest,
  responseBody: ZodPutRejectPoaTaskResponse,
});

export type PutTasksPoaRejectIdEndpoint = GetEndpointType<typeof putTasksPoaRejectIdEndpoint>;

export const putTasksAssignIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutAssignTaskRequest,
  responseBody: ZodPutAssignTaskResponse,
});

export type PutTasksAssignIdEndpoint = GetEndpointType<typeof putTasksAssignIdEndpoint>;

export type BoChange<T> = T extends object
  ? {
      [P in keyof T]?: BoChange<T[P]>;
    }
  : { old_value?: T; new_value?: T };

export const ZodTaskActionResponse = z.object({
  id: ZodId,
});

export type TaskActionResponse = z.infer<typeof ZodTaskActionResponse>;
